import {useCallback} from "react";
import {css, Theme} from "@emotion/react";

import {Badge} from "@pg-design/badge-module";
import {CenteredImage} from "@pg-design/centered-image";
import {mb, mr, mt, p} from "@pg-design/helpers-css";
import {LockOutlineIcon, PercentIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {head} from "@pg-mono/nodash";
import {rpAppLink} from "@pg-mono/rp-routes";
import {areaFormat, priceFormat} from "@pg-mono/string-utils";
import {LISTING_VENDOR_TEST_IDS} from "@pg-mono/test-ids";

import {getPropertyPlanAltText} from "../../comparison_tool/utils/get_property_plan_modal_title";
import {FavoriteButton} from "../../favorite/components/FavoriteButton";
import {useResponsiveLinkTarget} from "../../hooks/use_responsive_link_target";
import {useShouldShowPriceByOfferConfigAndUser} from "../../hooks/use_should_show_price_by_offer_config_and_user";
import {OfferType} from "../../offer/types/OfferType";
import {IPropertyListProperty} from "../../property/types/PropertyListProperty";
import {getCurrency} from "../../utils/get_currency";
import {OfferBoxBase} from "../OfferBoxBase";
import {PropertyViewRedirectButton} from "./PropertyViewRedirectButton";

interface IProps {
    onClick?: (property: IPropertyListProperty) => void;
    property: IPropertyListProperty;
    as?: "div";
    activeRedirectButton?: boolean;
}

export const PropertyBoxVariantBCD = (props: IProps) => {
    const {property, onClick, as, activeRedirectButton} = props;
    const {offer} = property;

    const propertyLink = rpAppLink.property.detail.base({
        vendorSlug: offer.vendor.slug,
        offerSlug: offer.slug,
        offerId: offer.id,
        propertyId: property.id
    });

    const target = useResponsiveLinkTarget();
    const externalPlanUrl = property?.external_plan_url;

    const planImage = externalPlanUrl || head(property.plan_image_pages)?.image.p_img_375x250;
    const altText = getPropertyPlanAltText(offer.name, property);

    const shouldShowPrice = useShouldShowPriceByOfferConfigAndUser({
        offerPriceVisibility: offer.configuration.price_visibility,
        vendorPriceVisibility: offer.vendor.configuration.price_visibility,
        vendorId: offer.vendor.id
    });

    const showPrice = shouldShowPrice && !!property.price;
    const onPropertyBoxClick = useCallback(() => {
        onClick && onClick(property);
    }, [property]);

    const isReserved = property.is_reserved;
    const isPromotion = property.is_promo;

    const Component = as || "a";

    return (
        <OfferBoxBase onClick={onPropertyBoxClick} css={propertyBox}>
            <Component href={propertyLink} css={propertyBoxLink} title={offer.name} target={target} data-testid={LISTING_VENDOR_TEST_IDS.PROPERTY_CONTAINER}>
                <FavoriteButton
                    offer={offer}
                    property={property}
                    css={subscribeButton}
                    display={{type: "icon"}}
                    dataTestId={LISTING_VENDOR_TEST_IDS.FAVOURITE_ICON}
                />
                <div css={badgeWrapper}>
                    {isReserved && (
                        <Badge variant="label_danger" icon={() => <LockOutlineIcon size="1.6" fill="white" css={mr(1)} />}>
                            <Text variant="info_txt_2" as="span" strong>
                                Rezerwacja
                            </Text>
                        </Badge>
                    )}
                    {!isReserved && isPromotion && (
                        <Badge variant="label_info" icon={() => <PercentIcon size="1.6" css={mr(1)} />}>
                            <Text variant="info_txt_2" as="span" strong>
                                Promocja
                            </Text>
                        </Badge>
                    )}
                </div>

                <CenteredImage
                    css={planWrap}
                    src={planImage}
                    alt={altText}
                    width={375}
                    height={250}
                    breakpoints={[{mediaWidth: "0", height: "200px", width: "100%"}]}
                    loading="lazy"
                />

                <div css={contentWrap}>
                    <ul css={propertyBoxInfoList}>
                        <li css={propertyBoxInfoListItem}>
                            <Text css={label} variant="info_txt_3" as="span">
                                Metraż
                            </Text>
                            <Text variant="info_txt_1" as="span">
                                {areaFormat(property.area, {precision: 2})}
                            </Text>
                        </li>

                        <li css={propertyBoxInfoListItem}>
                            <Text css={label} variant="info_txt_3" as="span">
                                Pokoje
                            </Text>
                            <Text variant="info_txt_1" as="span">
                                {property.rooms}
                            </Text>
                        </li>

                        <li css={propertyBoxInfoListItem}>
                            <Text css={label} variant="info_txt_3" as="span">
                                {offer.type === OfferType.HOUSE ? "Piętra" : "Piętro"}
                            </Text>
                            <Text variant="info_txt_1" as="span">
                                {offer.type === OfferType.HOUSE ? property.floors : property.floor === 0 ? "parter" : property.floor}
                            </Text>
                        </li>

                        {showPrice && (
                            <li css={propertyBoxInfoListItem}>
                                <Text css={label} variant="info_txt_3" as="span">
                                    Cena
                                </Text>
                                <Text variant="info_txt_1" as="span" strong>
                                    {priceFormat(property.price as number, {unit: getCurrency({currency: offer.currency})})}
                                </Text>
                            </li>
                        )}
                    </ul>
                </div>
                <PropertyViewRedirectButton property={property} isPlansList link={activeRedirectButton && as === "div" ? propertyLink : undefined} />
            </Component>
        </OfferBoxBase>
    );
};

//  Styles
const badgeLayerIndex = 30;

export const propertyBoxLink = css`
    cursor: pointer;
    display: flex;
    flex-direction: column;
    height: 100%;
    position: relative;

    &:hover,
    &:focus {
        color: inherit;
        text-decoration: none;
    }
`;

const propertyBox = css`
    height: 100%;
    ${p(2)};
`;

const planWrap = css`
    flex: 0 0 auto;

    & img {
        object-fit: contain;
        width: 100% !important; // override CenteredImage styles to fit image to container
        height: 100% !important; // override CenteredImage styles to fit image to container
    }
`;

const contentWrap = css`
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    ${mt(3)};
    ${mb(2)};
`;

const propertyBoxInfoList = css`
    padding-left: 0;
    display: flex;
    flex-direction: row;
    justify-content: space-evenly;
    align-items: center;
`;

const propertyBoxInfoListItem = css`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const label = (theme: Theme) => css`
    color: ${theme.colors.gray[700]};
    ${mb(0.5)}
`;

const badgeWrapper = css`
    position: absolute;
    z-index: ${badgeLayerIndex};
    display: flex;
    gap: 1rem;
`;

const subscribeButton = css`
    position: absolute;
    top: 0;
    right: 0;
    z-index: ${badgeLayerIndex};
`;
import {useCallback} from "react";
import {css, Theme} from "@emotion/react";

import {Badge} from "@pg-design/badge-module";
import {CenteredImage} from "@pg-design/centered-image";
import {mb, mr, mt, p} from "@pg-design/helpers-css";
import {LockOutlineIcon, PercentIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {head} from "@pg-mono/nodash";
import {rpAppLink} from "@pg-mono/rp-routes";
import {areaFormat, priceFormat} from "@pg-mono/string-utils";
import {LISTING_VENDOR_TEST_IDS} from "@pg-mono/test-ids";

import {getPropertyPlanAltText} from "../../comparison_tool/utils/get_property_plan_modal_title";
import {FavoriteButton} from "../../favorite/components/FavoriteButton";
import {useResponsiveLinkTarget} from "../../hooks/use_responsive_link_target";
import {useShouldShowPriceByOfferConfigAndUser} from "../../hooks/use_should_show_price_by_offer_config_and_user";
import {OfferType} from "../../offer/types/OfferType";
import {IPropertyListProperty} from "../../property/types/PropertyListProperty";
import {getCurrency} from "../../utils/get_currency";
import {OfferBoxBase} from "../OfferBoxBase";
import {PropertyViewRedirectButton} from "./PropertyViewRedirectButton";

interface IProps {
    onClick?: (property: IPropertyListProperty) => void;
    property: IPropertyListProperty;
    as?: "div";
    activeRedirectButton?: boolean;
}

export const PropertyBoxVariantBCD = (props: IProps) => {
    const {property, onClick, as, activeRedirectButton} = props;
    const {offer} = property;

    const propertyLink = rpAppLink.property.detail.base({
        vendorSlug: offer.vendor.slug,
        offerSlug: offer.slug,
        offerId: offer.id,
        propertyId: property.id
    });

    const target = useResponsiveLinkTarget();
    const externalPlanUrl = property?.external_plan_url;

    const planImage = externalPlanUrl || head(property.plan_image_pages)?.image.p_img_375x250;
    const altText = getPropertyPlanAltText(offer.name, property);

    const shouldShowPrice = useShouldShowPriceByOfferConfigAndUser({
        offerPriceVisibility: offer.configuration.price_visibility,
        vendorPriceVisibility: offer.vendor.configuration.price_visibility,
        vendorId: offer.vendor.id
    });

    const showPrice = shouldShowPrice && !!property.price;
    const onPropertyBoxClick = useCallback(() => {
        onClick && onClick(property);
    }, [property]);

    const isReserved = property.is_reserved;
    const isPromotion = property.is_promo;

    const Component = as || "a";

    return (
        <OfferBoxBase onClick={onPropertyBoxClick} css={propertyBox}>
            <Component href={propertyLink} css={propertyBoxLink} title={offer.name} target={target} data-testid={LISTING_VENDOR_TEST_IDS.PROPERTY_CONTAINER}>
                <FavoriteButton
                    offer={offer}
                    property={property}
                    css={subscribeButton}
                    display={{type: "icon"}}
                    dataTestId={LISTING_VENDOR_TEST_IDS.FAVOURITE_ICON}
                />
                <div css={badgeWrapper}>
                    {isReserved && (
                        <Badge variant="label_danger" icon={() => <LockOutlineIcon size="1.6" fill="white" css={mr(1)} />}>
                            <Text variant="info_txt_2" as="span" strong>
                                Rezerwacja
                            </Text>
                        </Badge>
                    )}
                    {!isReserved && isPromotion && (
                        <Badge variant="label_info" icon={() => <PercentIcon size="1.6" css={mr(1)} />}>
                            <Text variant="info_txt_2" as="span" strong>
                                Promocja
                            </Text>
                        </Badge>
                    )}
                </div>

                <CenteredImage
                    css={planWrap}
                    src={planImage}
                    alt={altText}
                    width={375}
                    height={250}
                    breakpoints={[{mediaWidth: "0", height: "200px", width: "100%"}]}
                    loading="lazy"
                />

                <div css={contentWrap}>
                    <ul css={propertyBoxInfoList}>
                        <li css={propertyBoxInfoListItem}>
                            <Text css={label} variant="info_txt_3" as="span">
                                Metraż
                            </Text>
                            <Text variant="info_txt_1" as="span">
                                {areaFormat(property.area, {precision: 2})}
                            </Text>
                        </li>

                        <li css={propertyBoxInfoListItem}>
                            <Text css={label} variant="info_txt_3" as="span">
                                Pokoje
                            </Text>
                            <Text variant="info_txt_1" as="span">
                                {property.rooms}
                            </Text>
                        </li>

                        <li css={propertyBoxInfoListItem}>
                            <Text css={label} variant="info_txt_3" as="span">
                                {offer.type === OfferType.HOUSE ? "Piętra" : "Piętro"}
                            </Text>
                            <Text variant="info_txt_1" as="span">
                                {offer.type === OfferType.HOUSE ? property.floors : property.floor === 0 ? "parter" : property.floor}
                            </Text>
                        </li>

                        {showPrice && (
                            <li css={propertyBoxInfoListItem}>
                                <Text css={label} variant="info_txt_3" as="span">
                                    Cena
                                </Text>
                                <Text variant="info_txt_1" as="span" strong>
                                    {priceFormat(property.price as number, {unit: getCurrency({currency: offer.currency})})}
                                </Text>
                            </li>
                        )}
                    </ul>
                </div>
                <PropertyViewRedirectButton property={property} isPlansList link={activeRedirectButton && as === "div" ? propertyLink : undefined} />
            </Component>
        </OfferBoxBase>
    );
};

//  Styles
const badgeLayerIndex = 30;

export const propertyBoxLink = css`
    cursor: pointer;
    display: flex;
    flex-direction: column;
    height: 100%;
    position: relative;

    &:hover,
    &:focus {
        color: inherit;
        text-decoration: none;
    }
`;

const propertyBox = css`
    height: 100%;
    ${p(2)};
`;

const planWrap = css`
    flex: 0 0 auto;

    & img {
        object-fit: contain;
        width: 100% !important; // override CenteredImage styles to fit image to container
        height: 100% !important; // override CenteredImage styles to fit image to container
    }
`;

const contentWrap = css`
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    ${mt(3)};
    ${mb(2)};
`;

const propertyBoxInfoList = css`
    padding-left: 0;
    display: flex;
    flex-direction: row;
    justify-content: space-evenly;
    align-items: center;
`;

const propertyBoxInfoListItem = css`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const label = (theme: Theme) => css`
    color: ${theme.colors.gray[700]};
    ${mb(0.5)}
`;

const badgeWrapper = css`
    position: absolute;
    z-index: ${badgeLayerIndex};
    display: flex;
    gap: 1rem;
`;

const subscribeButton = css`
    position: absolute;
    top: 0;
    right: 0;
    z-index: ${badgeLayerIndex};
`;
